import PropTypes from "prop-types";

/**
 * CodeLivePreview - A sandbox iframe to render React code using Babel Standalone
 */
export default function CodeLivePreview({
	code,
	theme = {},
	variables = {},
	height = "600px",
	codeType = "react",
}) {
	// Process code:
	// 1. Replace tokens {{name}}, {{client}}, etc.
	// 2. Escape backticks
	// 3. Remove imports
	let processedCode = code || "";

	// Token replacement
	Object.entries(variables).forEach(([key, val]) => {
		const regex = new RegExp(`{{${key}}}`, "g");
		processedCode = processedCode.replace(regex, val);
	});

	// Parse imported lucide icons
	const importedIcons = new Set();
	const importRegex =
		/import\s+\{([\s\S]*?)\}\s+from\s+['"]lucide-react['"];?/g;
	let match = importRegex.exec(processedCode);
	while (match !== null) {
		const icons = match[1]
			.split(",")
			.map((x) =>
				x
					.replace(/\/\*[\s\S]*?\*\//g, "")
					.replace(/\/\/.*$/gm, "")
					.trim(),
			)
			.filter((x) => x);
		icons.forEach((icon) => {
			const parts = icon.split(/\s+as\s+/);
			const iconName = parts[0].trim();
			importedIcons.add(iconName);
		});
		match = importRegex.exec(processedCode);
	}

	const iconDeclarations =
		importedIcons.size > 0
			? `const { ${Array.from(importedIcons).join(", ")} } = Icons;`
			: "";

	const cleanedCode = processedCode
		.replace(
			/import\s+[\s\S]*?from\s+['"].*?['"];?/g,
			(match) => `// Fixed: ${match.split("\n")[0]}...`,
		)
		.replace(/export\s+default\s+.*?;?/g, "// Export cleaned");

	const isHtml = codeType === "html";

	const srcDoc = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <script src="https://cdn.tailwindcss.com"></script>
          
          <!-- React & ReactDOM UMD -->
          <script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
          <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
          
          <!-- Lucide Icons Library -->
          <script src="https://unpkg.com/lucide@latest"></script>
          
          <!-- Babel Standalone -->
          <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
          
          <style>
            body { margin: 0; background: #0B0F19; color: #cbd5e1; font-family: sans-serif; overflow-x: hidden; }
            #root { min-height: 100vh; }
            ::-webkit-scrollbar { width: 8px; }
            ::-webkit-scrollbar-track { background: #0f172a; }
            ::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }
            .error-box { padding: 20px; background: #450a0a; color: #fecaca; border: 1px solid #991b1b; margin: 10px; border-radius: 8px; font-family: monospace; font-size: 11px; white-space: pre-wrap; }
            
            /* Theme injection as CSS variables */
            :root {
              --primary-color: ${theme.primaryColor || "#0ea5e9"};
              --secondary-color: ${theme.secondaryColor || "#8b5cf6"};
              --font-family: ${theme.fontFamily || "Inter"};
              --border-radius: ${theme.borderRadius || "12px"};
            }
          </style>
        </head>
        <body>
          <div id="root">${isHtml ? cleanedCode : ""}</div>

          <script type="text/babel">
            // Global Error Handling
            window.onerror = function(msg) {
              const root = document.getElementById('root');
              if (root) root.innerHTML = '<div class="error-box"><strong>Erro de Execução:</strong><br/>' + msg + '</div>';
            };

            // Theme object available to JS
            const theme = ${JSON.stringify(theme)};

            // Provide React basics
            const { useState, useEffect, useMemo, useCallback, useRef } = React;
            
            const Icons = {};
            if (window.lucide && lucide.icons) {
              Object.keys(lucide.icons).forEach(name => {
                Icons[name] = (props) => {
                  const iconRef = React.useRef(null);
                  React.useEffect(() => {
                    if (iconRef.current && lucide.icons[name]) {
                      lucide.createIcons({
                        icons: { [name]: lucide.icons[name] },
                        attrs: { ...props, class: props.className || "" }
                      });
                    }
                  }, [props]);
                  return <i ref={iconRef} data-lucide={name} className={props.className || ""}></i>;
                };
              });
            }

            try {
              if (${isHtml}) {
                // Initialize Lucide icons on the static HTML
                if (window.lucide) {
                  lucide.createIcons();
                }
              } else {
                /* ICON_DECLARATIONS */
                /* CODE_PLACEHOLDER */

                const container = document.getElementById('root');
                if (typeof App !== 'undefined') {
                  const root = ReactDOM.createRoot(container);
                  root.render(<App />);
                } else {
                  container.innerHTML = '<div class="error-box">Erro: Nao encontramos o componente "App" no seu codigo.</div>';
                }
              }
            } catch (err) {
              document.getElementById('root').innerHTML = '<div class="error-box"><strong>Erro de Compilação:</strong><br/>' + err.message + '</div>';
            }
          </script>
        </body>
      </html>
    `
		.replace("/* ICON_DECLARATIONS */", () => iconDeclarations)
		.replace("/* CODE_PLACEHOLDER */", () => (isHtml ? "" : cleanedCode));

	return (
		<div
			className="w-full bg-[#0B0F19] rounded-xl border border-white/5 overflow-hidden shadow-2xl relative"
			style={{ height }}
		>
			<div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-purple-500 z-10 print-hide"></div>
			<iframe
				title="Code Preview"
				srcDoc={srcDoc}
				className="w-full h-full border-0"
				sandbox="allow-scripts allow-modals"
			/>
		</div>
	);
}

CodeLivePreview.propTypes = {
	code: PropTypes.string.isRequired,
	theme: PropTypes.object,
	variables: PropTypes.object,
	height: PropTypes.string,
	codeType: PropTypes.string,
};
